import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Compass, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { homeForRole, spaceForRole } from '@/lib/homeForRole';
import { candidateRoutes, suggestRoutes } from '@/lib/routeSuggestions';
import { BRAND } from '@/config/brand';

/**
 * The page for /unauthorized and for an unknown route (B3, F4). Its main
 * button leads back to the reader's own space through the same rule as the
 * guards (homeForRole), not to the public homepage.
 *
 * W6 (B3-unauthorized, B3-404, QW-h11): /unauthorized names the page asked
 * for (RequireRole passes it in the location state), the reader's role, and
 * says the page is reserved to administrators; the 404 suggests the pages of
 * the reader's space closest to the mistyped address.
 */
export function ErrorPage({ kind }: { kind: 'unauthorized' | 'notFound' }) {
  const { t } = useTranslation(['common', 'admin', 'dialer', 'pages']);
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const active = useAuthStore((s) => s.activeMembership);
  const role = active?.role ?? null;
  const identity = user ? { isSuperAdmin: user.isSuperAdmin, role } : null;
  const home = identity ? homeForRole(identity) : '/';
  const space = identity ? spaceForRole(identity) : null;

  const from = (location.state as { from?: string } | null)?.from;
  const suggestions = kind === 'notFound' ? suggestRoutes(location.pathname, candidateRoutes(space, role), home) : [];
  const Icon = kind === 'unauthorized' ? ShieldAlert : Compass;
  const title = kind === 'unauthorized' ? t('common:unauthorized.title') : t('common:notFound.title');

  return (
    <main className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--vf-surface-1)' }}>
      <div className="w-full max-w-md rounded-vf border p-8 text-center space-y-4" style={{ background: 'var(--vf-bg)', borderColor: 'var(--vf-border)' }}>
        <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{BRAND.name}</p>
        <Icon className="mx-auto h-8 w-8" style={{ color: 'var(--vf-accent-text)' }} aria-hidden />
        <h1 className="text-[22px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>{title}</h1>
        {kind === 'unauthorized' ? (
          <div className="space-y-2 text-[14px]" style={{ color: 'var(--vf-text-2)' }}>
            <p>{from ? t('common:errorPage.reservedPage', { page: from }) : t('common:errorPage.reserved')}</p>
            {user && (
              <p>{t('common:errorPage.signedInAs', { role: t(`common:errorPage.roles.${user.isSuperAdmin ? 'super_admin' : role ?? 'none'}`) })}</p>
            )}
            <p>{t('common:errorPage.askAdmin')}</p>
          </div>
        ) : (
          <p className="text-[14px]" style={{ color: 'var(--vf-text-2)' }}>
            {`${t('common:notFound.bodyPrefix')} « ${location.pathname} » ${t('common:notFound.bodySuffix')}`}
          </p>
        )}
        {suggestions.length > 0 && (
          <nav aria-label={t('common:errorPage.suggestions')} className="text-left">
            <p className="mb-1 text-[12px] font-semibold" style={{ color: 'var(--vf-text-3)' }}>{t('common:errorPage.suggestions')}</p>
            <ul className="space-y-1">
              {suggestions.map((s) => (
                <li key={s.path}>
                  <a href={s.path} className="inline-flex min-h-[44px] items-center text-[14px] underline" style={{ color: 'var(--vf-accent-text)' }}>{t(s.labelKey)}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="flex flex-wrap justify-center gap-2 pt-2">
          <Button asChild>
            <a href={home}>{user ? t('common:errorPage.backToSpace') : t('common:notFound.goHome')}</a>
          </Button>
          {kind === 'unauthorized' && (
            <Button asChild variant="outline">
              <a href="/Login">{t('common:unauthorized.reconnect')}</a>
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}

export default ErrorPage;
